import { useNavigate } from 'react-router';
import { api } from '../api/client';
import { Coins, Empty, ErrorState, Loading, TopBar } from '../components/ui';
import { useT } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';
import { realtime } from '../store/session';
import { toast } from '../store/ui';
import { useEffect } from 'react';
import { useGame } from '../store/game';

interface Tournament {
  id: string;
  name: string;
  gameType: string;
  format: string;
  entryFee: number;
  startingChips: number;
  maxPlayers: number;
  entrants: number;
  prizePool: number;
  prizeStructure: number[];
  status: string;
}

export function Tournaments() {
  const t = useT();
  const nav = useNavigate();
  const data = useAsync(() => api.get<{ enabled: boolean; tournaments: Tournament[] }>('/api/v1/tournaments'), []);
  useEffect(() => {
    const off = realtime.on('notify.v1', (d) => {
      const n = d as { type: string; data: { tableId?: string } };
      if (n.type === 'tournament_started' && n.data.tableId) {
        useGame.setState({ mm: { state: 'MATCHED', tableConfigId: '', elapsedMs: 0, timeoutMs: 0, playersFound: 0, playersNeeded: 0, tableId: n.data.tableId } });
        nav(`/table/${n.data.tableId}`);
      }
    });
    return () => void off();
  }, [nav]);
  const register = async (id: string) => {
    try {
      const r = await api.post<{ entrants: number; started: boolean }>(`/api/v1/tournaments/${id}/register`);
      toast(r.started ? 'Tournament starting!' : 'Registered — waiting for players', 'success');
      data.reload();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  const unregister = async (id: string) => {
    try {
      await api.del(`/api/v1/tournaments/${id}/register`);
      toast('Unregistered — entry refunded', 'success');
      data.reload();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  return (
    <main className="page">
      <TopBar title={t('lobby.tournaments')} onBack={() => nav(-1)} />
      {data.loading && <Loading />}
      {data.error && <ErrorState message={data.error} onRetry={data.reload} />}
      {data.data && !data.data.enabled && <Empty emoji="🏆" title={t('common.comingSoon')} />}
      {data.data?.enabled && data.data.tournaments.length === 0 && <Empty emoji="🏆" title="No tournaments open right now" />}
      <div className="col">
        {data.data?.enabled &&
          data.data.tournaments.map((tr) => (
            <div key={tr.id} className="card col">
              <div className="spread">
                <strong>{tr.name}</strong>
                <span className={`pill ${tr.status === 'RUNNING' ? 'live' : 'accent'}`}>{tr.status === 'RUNNING' ? 'Running' : `${tr.entrants}/${tr.maxPlayers}`}</span>
              </div>
              <div className="row wrap dim" style={{ fontSize: 'var(--fs-sm)' }}>
                <span>Entry <Coins amount={tr.entryFee} size={14} /></span>
                <span>Prize pool <Coins amount={tr.prizePool} size={14} /></span>
                <span>Payouts {tr.prizeStructure.map((p) => `${p}%`).join(' / ')}</span>
              </div>
              {tr.status === 'REGISTERING' && (
                <div className="row">
                  <button className="btn primary grow" onClick={() => void register(tr.id)}>Register</button>
                  <button className="btn ghost" onClick={() => void unregister(tr.id)}>Unregister</button>
                </div>
              )}
            </div>
          ))}
      </div>
    </main>
  );
}
